import styled from '@emotion/styled'
import {
    ApiBrandBenefit,
    ApiFullLandingPageLink,
    ApiShopperLandingPageLink,
    INLINE_CTA_ACTION,
    VLPVariant,
    isCartActionButtonType,
    resolveInlineCtaButtonText,
} from '@storefronts/common-types'
import type { LandingPageModel } from '../../../../models/landingPageModel'
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type Dispatch,
    type MutableRefObject,
    type SetStateAction,
} from 'react'
import { useRouter } from 'next/router'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { BrandChatQuestions } from '../../brand-chat/BrandChatQuestions'
import {
    ACCENT_FILL_COLOR_VAR,
    ACCENT_TEXT_COLOR_VAR,
    CustomizationContext,
    PRIMARY_FONT_CSS_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_CTA_BORDER_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../../context/CustomizationContextProvider'
import { useShopPayButtons } from '../../../hooks/useShopPayButtons'
import { OverrideContext } from '../../../context/overrideContext'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import type { CSSObject } from '@emotion/react'
import {
    HEADER_HEIGHT_VAR,
    inScrollingLayout,
    scaledFontSize,
    ScrollingLayout,
    SECTION_GUTTER_VAR,
    sectionMedia,
    SectionMedia,
} from '../../../style/scrollingLayout'
import { onHover } from '../../../style/interactiveStates'
import { Constants } from '../../../style/styleConstants'
import { getTypographyRoleStyle } from '../../../style/typography'
import { MIN_VISIBLE_REVIEW_COUNT } from '../../../constants'
import {
    getDisplayData,
    getRequiresSellingPlan,
    getSellingPlanGroups,
    getSubscriptionOnlyVariantCorrection,
    resolveDefaultPdpVariant,
    resolveProductCardImageUrls,
} from '../../../utils/productDisplay'
import { getReviewSummary } from '../../../utils/getReviewSummary'
import { getProductPrices } from '../../../utils/priceFormat'
import { buildCartVariant } from '../../../utils/cartTools'
import { BenefitIcon } from '../../shared/BenefitIcon'
import { InStockIndicator } from '../../shared/InStockIndicator'
import { VariantSelector } from '../../shared/variant-options/VariantSelector'
import { PoweredByFooter } from '../../shared/PoweredByFooter'
import { ProductDrawerCTA } from '../full-screen/ProductDrawerCTA'
import { ReviewsStars } from '../reviews/Reviews'
import { ProductTabContainer } from '../TabContainer'
import { PdpSubscriptionSelector } from './PdpSubscriptionSelector'
import {
    PDP_GALLERY_HEIGHT_VAR,
    ProductImageCarousel,
} from './ProductImageCarousel'
import { ProductOptionGuideRow } from './ProductOptionGuideRow'
import {
    getAdjustedSelectedSellingPlanId,
    getDefaultPdpSellingPlanId,
    getPdpDisplayPricing,
} from '../../../utils/pdpSubscriptions'
import {
    PageContext,
    type ActiveLPLinkOptions,
} from '../../../context/PageContextProvider'
import { PriceMainRow, CompareAtPriceRow } from './ProductPriceDisplay'
import { wideTwoColumn } from './productColumns'

const COLUMN_GAP_PX = 24

const TWO_COLUMN_GALLERY_HEIGHT_PX = 364

/** Below this the two columns are each too narrow to be worth the split. */
const TWO_COLUMN_MIN_PX = 550
const twoColumn = sectionMedia(TWO_COLUMN_MIN_PX)

/**
 * Favours the buy box, where the title, price, options and CTA all have to
 * fit; the gallery only has to stay recognisable.
 */
const NARROW_TWO_COLUMN_TEMPLATE = 'minmax(0, 40fr) minmax(0, 60fr)'

/** Clearance between the pinned gallery and the header it sits under. */
const STICKY_GALLERY_GAP_PX = 16

const HEADER_CONTAINER_NAME = 'product-header'

/**
 * Below this a title set beside the price breaks to a word a line, so the
 * three stack instead. The header is 480px wide once the buy box has a fixed
 * column, and a fraction of the prose measure below that.
 */
const SPLIT_HEADER_MIN_PX = 430
const wideHeader = `@container ${HEADER_CONTAINER_NAME} (min-width: ${SPLIT_HEADER_MIN_PX}px)`

/** Drops a width-driven rule where the caller has asked for one column. */
const whenSplit = (singleColumn: boolean, styles: CSSObject): CSSObject =>
    singleColumn ? {} : styles

const NavGridInnerContainer = styled.div<{
    isInOuterScrollContainer: boolean
    singleColumn: boolean
}>(({ isInOuterScrollContainer, singleColumn }) => ({
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    width: '100%',
    bottom: 0,
    background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    paddingTop: 0,
    ...(isInOuterScrollContainer
        ? {}
        : {
              overflowY: 'auto',
              minHeight: 0,
              '&::-webkit-scrollbar': {
                  display: 'none',
              },
          }),
    ...inScrollingLayout({
        maxWidth: ScrollingLayout.PROSE_MAX_WIDTH,
        marginInline: 'auto',
    }),
    ...whenSplit(singleColumn, {
        [twoColumn]: {
            display: 'grid',
            gridTemplateColumns: NARROW_TWO_COLUMN_TEMPLATE,
            columnGap: COLUMN_GAP_PX,
            alignItems: 'start',
            // In the overlay this grid is a flex child of a scroll box, so it
            // is handed the panel's full height; without this the rows share
            // out the surplus and each gains a band of empty space.
            alignContent: 'start',
            // The grid owns the gutter so both columns share one measure, and
            // body sections stop adding a second one inside it.
            paddingInline: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
            [SECTION_GUTTER_VAR]: '0px',
        },
        [SectionMedia.XL]: {
            gridTemplateColumns: `minmax(0, 1fr) ${ScrollingLayout.CTA_MAX_WIDTH}px`,
            // Wider than the prose measure, but still the page's column: the
            // overlay hands the product the whole window.
            maxWidth: ScrollingLayout.CONTENT_MAX_WIDTH,
            [PDP_GALLERY_HEIGHT_VAR]: `${TWO_COLUMN_GALLERY_HEIGHT_PX}px`,
        },
    }),
}))

const GalleryColumn = styled.div({
    width: '100%',
    [twoColumn]: {
        gridColumn: 1,
        gridRow: 1,
    },
    // Spanning both rows, stretched against the grid's `align-items: start`,
    // is what gives a pinned gallery the height of the product to travel.
    [wideTwoColumn]: {
        gridRow: '1 / 3',
        alignSelf: 'stretch',
    },
})

/**
 * Sticking this inner element rather than the grid item is what stops the
 * gallery at the bottom of the buy box: Chromium constrains a sticky grid item
 * to the whole grid rather than to its own area, so a gallery stuck directly
 * to the column carries on down over the full-width body sections below it.
 */
const GalleryPin = styled.div<{ pinned: boolean }>(({ pinned }) => ({
    width: '100%',
    [wideTwoColumn]: pinned
        ? {
              position: 'sticky',
              top: `calc(var(${HEADER_HEIGHT_VAR}, 0px) + ${STICKY_GALLERY_GAP_PX}px)`,
          }
        : {},
}))

/** `display: contents` joins the two-column grid without an extra wrapper. */
const ProductSurface = styled.div<{ singleColumn: boolean }>(
    ({ singleColumn }) => ({
        backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        ...whenSplit(singleColumn, {
            [twoColumn]: {
                display: 'contents',
            },
        }),
    }),
)

const ValueBadge = styled.div({
    backgroundColor: `var(${ACCENT_FILL_COLOR_VAR})`,
    padding: '8px 10px',
    color: `var(${ACCENT_TEXT_COLOR_VAR})`,
    borderRadius: '0.2rem',
    fontSize: scaledFontSize(12),
    ...getTypographyRoleStyle('badgeText', { fontWeight: 300 }),
    margin: '4px 6px 4px 0px',
})

const AddToCartBrandBenefitContainer = styled.div({
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
    padding: '20px 0px 0px',
    gap: '4px',
})

const AddToCartBrandBenefit = styled.span({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    ...getTypographyRoleStyle('captionText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        lineHeight: '19px',
    }),
    fontSize: scaledFontSize(14),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    padding: '8px 0px',
    gap: '10px',
})

const FullDetailsLink = styled.p({
    ...getTypographyRoleStyle('bodyText'),
    textAlign: 'center',
    marginTop: 10,
    fontSize: scaledFontSize(14),
    cursor: 'pointer',
    ...onHover({ textDecoration: 'underline' }),
})

const BrandBenefits: React.FC<{ benefits: ApiBrandBenefit[] }> = ({
    benefits,
}) => {
    if (benefits.length === 0) return null
    return (
        <AddToCartBrandBenefitContainer>
            {benefits.map((benefit) => (
                <AddToCartBrandBenefit key={benefit.id}>
                    <BenefitIcon type={benefit.type} strokeWidth={1.5} />
                    {benefit.description}
                </AddToCartBrandBenefit>
            ))}
        </AddToCartBrandBenefitContainer>
    )
}

export const ProductContentBlock: React.FC<{
    link: ApiFullLandingPageLink
    variantShopifyProductId?: string | null
    showBodyContent: boolean
    showReviews: boolean
}> = ({ link, variantShopifyProductId, showBodyContent, showReviews }) => {
    const { linkContentOverride } = useContext(OverrideContext)

    if (linkContentOverride) {
        return (
            <ProductTabContainer
                link={link}
                variantShopifyProductId={variantShopifyProductId}
                content={linkContentOverride}
                showBodyContent={showBodyContent}
                showReviews={showReviews}
                compact={true}
            />
        )
    }
    if (link.bodyContent) {
        return (
            <ProductTabContainer
                link={link}
                variantShopifyProductId={variantShopifyProductId}
                content={link.bodyContent}
                showBodyContent={showBodyContent}
                showReviews={showReviews}
                compact={true}
            />
        )
    }
    return null
}

/** Separates the title from a gallery stacked above it. */
const FLUSH_TITLE_GAP_PX = 12

const productColumn = (flush: boolean): CSSObject => ({
    width: '100%',
    boxSizing: 'border-box',
    paddingInline: flush ? 0 : Constants.CONTENT_BLOCK_MOBILE_MARGIN,
    marginBottom: 0,
    '@media (max-width: 360px)': {
        paddingInline: flush ? 0 : 16,
    },
})

const ProductHeader = styled.div<{ flush: boolean; singleColumn: boolean }>(
    ({ flush, singleColumn }) => ({
        ...productColumn(flush),
        marginTop: flush ? FLUSH_TITLE_GAP_PX : 16,
        // Whether the title can hold a column beside the price depends on the
        // header's own width: between the two-column and XL breakpoints it is
        // only a fraction of the section it sits in.
        ...inScrollingLayout({
            containerType: 'inline-size',
            containerName: HEADER_CONTAINER_NAME,
        }),
        ...whenSplit(singleColumn, {
            [twoColumn]: {
                gridColumn: 2,
                gridRow: 1,
                paddingInline: 0,
                marginTop: 0,
            },
        }),
    }),
)

const ProductBuyBox = styled.div<{ flush: boolean; singleColumn: boolean }>(
    ({ flush, singleColumn }) => ({
        ...productColumn(flush),
        ...whenSplit(singleColumn, {
            [twoColumn]: {
                gridColumn: '1 / -1',
                gridRow: 2,
                paddingInline: 0,
            },
            [wideTwoColumn]: {
                gridColumn: 2,
            },
        }),
    }),
)

/**
 * The rating belongs to the title — it reads as part of what the product is —
 * so it takes the line under it and leaves the price a column of its own
 * wherever there is room for one.
 */
const TitlePriceRow = styled.div({
    display: 'grid',
    boxSizing: 'border-box',
    alignItems: 'start',
    columnGap: 12,
    rowGap: 8,
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateAreas: '"title price" "reviews reviews"',
    // Too narrow for a price beside the title, so the three stack.
    [twoColumn]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateAreas: '"title" "reviews" "price"',
    },
    [wideHeader]: {
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gridTemplateAreas: '"title price" "reviews price"',
        rowGap: 6,
    },
})

const TitleCell = styled.div({
    gridArea: 'title',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 0,
})

const ReviewsCell = styled.div({
    gridArea: 'reviews',
    minWidth: 0,
})

const PriceCell = styled.div({
    gridArea: 'price',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    [twoColumn]: {
        alignItems: 'flex-start',
    },
    [wideHeader]: {
        alignItems: 'flex-end',
    },
})

/**
 * Deliberately uncapped: the product's own sections cap themselves at the
 * prose measure or bleed past it on their own terms, so a cap here would hold
 * the ones that bleed to the measure the body text keeps.
 */
const ProductContentBlockWrapper = styled.div({
    width: '100%',
    boxSizing: 'border-box',
    [twoColumn]: {
        gridColumn: '1 / -1',
    },
})

/** Belongs under the whole product, not under the gallery column. */
const FooterRow = styled.div({
    [twoColumn]: {
        gridColumn: '1 / -1',
    },
})

export const ProductContent: React.FC<{
    lpLink: ApiFullLandingPageLink
    activeVariant: VLPVariant | null
    setActiveVariant: (v: VLPVariant) => any
    reviewsRef: React.MutableRefObject<HTMLDivElement | null>
    price: number
    comparePrice: number | null
    discountPercentage?: number | null
    hideCartOverride: boolean
    showBodyContent?: boolean
    showReviews?: boolean
    renderCTAInline?: boolean
    inlineCTAActions?: INLINE_CTA_ACTION[]
    inlineButtonText?: string
    displayPoweredByFooter?: boolean
    maxImageHeight?: string
    onTitleClick?: () => void
    /**
     * When true, the component does not create its own vertical scroll box
     * and lets whichever ancestor scrolls own the scrolling. Set this for any
     * embedded placement where the page (or a drawer above it) is what
     * scrolls, so sections inside the product that bleed past the column are
     * not clipped by an inner scroll boundary.
     */
    isInOuterScrollContainer?: boolean
    /**
     * The actual scrollable element to reset on product change. Only useful
     * when it is genuinely scrollable — a ref to a non-scrolling wrapper
     * would leave the caller thinking scroll-reset happened when it did not.
     * Falls back to the internal container.
     */
    outerScrollContainerRef?: React.RefObject<HTMLElement | null>
    /**
     * When true, carousel hero uses Next/Image `priority` (preload). Default true
     * for full PDP; inline sections pass false when below early-section cutoff.
     */
    prioritizeHeroImage?: boolean
    /** When provided, overrides the product-level body badges for this inline placement. */
    bodyBadgesOverride?: string[]
    /** Caller owns the product's inset, horizontally and above it. */
    flushContent?: boolean
    /**
     * Keeps the gallery above the rest of the product however wide the column
     * is, for a placement that is already beside something else.
     */
    singleColumn?: boolean
    /**
     * The product is part of the page rather than presented in an overlay, so
     * the page's own scroll can carry a gallery taller than one screen.
     */
    embedded?: boolean
    /** Brand chat question chips under the inline CTA. */
    showBrandChat?: boolean
    /** Selling plan chosen on the PDP; see {@link ProductData.selectedSellingPlanId}. */
    selectedSellingPlanId?: string | null
    onSellingPlanSelect?: (sellingPlanId: string | null) => void
    // eslint-disable-next-line complexity
}> = ({
    activeVariant,
    setActiveVariant,
    reviewsRef,
    price,
    comparePrice,
    discountPercentage,
    lpLink,
    selectedSellingPlanId = null,
    onSellingPlanSelect,
    hideCartOverride,
    showBodyContent = true,
    showReviews = true,
    renderCTAInline = false,
    inlineCTAActions = [INLINE_CTA_ACTION.ADD_TO_CART],
    inlineButtonText,
    displayPoweredByFooter = true,
    maxImageHeight,
    onTitleClick,
    isInOuterScrollContainer = false,
    outerScrollContainerRef,
    prioritizeHeroImage = true,
    bodyBadgesOverride,
    flushContent = false,
    singleColumn = false,
    embedded = false,
    showBrandChat = false,
}) => {
    const { setSelectedCartActionVariantShopifyId } = useContext(PageContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { landingPage } = useContext(ViddyLPContext)
    const {
        reviewStarColorOverride,
        accentFillColor,
        secondaryCtaFillColor,
        secondaryCtaTextColor,
    } = useContext(CustomizationContext)
    const displayShopPayButtons = useShopPayButtons()
    const productInnerContainer = useRef<HTMLDivElement>(null)

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (lpLink) {
            const scrollTarget =
                outerScrollContainerRef?.current ??
                productInnerContainer.current
            scrollTarget?.scrollTo(0, 0)
        }
    }, [lpLink, outerScrollContainerRef])

    // Variant-aware display data (title, reviews, reviewContent, carousel, body)
    const displayData = useMemo(
        () => getDisplayData(lpLink, activeVariant?.shopifyProductId),
        [lpLink, activeVariant?.shopifyProductId],
    )
    const productOptionGuide = displayData.productOptionGuide

    // Stable link for tabs/reviews: same reference until display data or variant changes
    const displayLink = useMemo(
        () => ({
            ...lpLink,
            title: displayData.title ?? lpLink.title,
            reviews: displayData.reviews ?? lpLink.reviews,
            bodyContent: displayData.bodyContent,
            bodyBadges: displayData.bodyBadges,
            reviewContent: displayData.reviewContent,
            productOptionGuide:
                displayData.productOptionGuide ?? lpLink.productOptionGuide,
        }),
        [lpLink, displayData],
    )

    const carouselImageUrls: string[] = useMemo(() => {
        return resolveProductCardImageUrls(activeVariant, lpLink, displayData)
    }, [activeVariant, lpLink, displayData])

    const carouselInstanceKey = useMemo(
        () => carouselImageUrls.join('|'),
        [carouselImageUrls],
    )

    const productValueBadges = useMemo(() => {
        if (!lpLink) return []

        const badges =
            bodyBadgesOverride !== undefined
                ? bodyBadgesOverride
                : displayData.bodyBadges

        if (!badges || badges.length === 0) {
            return []
        }

        return badges.map((badgeText, idx) => {
            return <ValueBadge key={idx}>{badgeText}</ValueBadge>
        })
    }, [lpLink, displayData.bodyBadges, bodyBadgesOverride])

    const onReviewsClick = () => {
        Analytics.TRACK_REVIEWS_SUMMARY_CLICK('pdp')
        reviewsRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const showVariantSelector = !hideCartOverride

    // Shopify applies the selling plan discount to the variant's own price, not
    // to a landing-page/product promotion layered on top, and those promos
    // don't stack with subscriptions in the cart. Base the subscription price
    // on the variant price so the displayed amount matches the cart.
    const subscriptionBasePriceCents = activeVariant?.price.price ?? price
    const sellingPlanGroups = getSellingPlanGroups(
        lpLink,
        activeVariant?.shopifyProductId,
        activeVariant?.shopifyId,
    )
    const requiresSellingPlan = getRequiresSellingPlan(
        lpLink,
        activeVariant?.shopifyProductId,
    )
    const {
        priceCents: displayPrice,
        compareAtPriceCents: displayComparePrice,
        discountPercentage: displayDiscountPercentage,
    } = getPdpDisplayPricing({
        oneTimePriceCents: price,
        listPriceCents: comparePrice,
        oneTimeDiscountPercentage: discountPercentage ?? null,
        subscriptionBasePriceCents,
        sellingPlanGroups,
        selectedSellingPlanId,
    })

    const { reviewRating: fallbackRating, reviewCount: fallbackCount } =
        getReviewSummary(landingPage, lpLink)
    const reviewRating = displayData.reviews?.rating ?? fallbackRating
    const reviewCount = displayData.reviews?.count ?? fallbackCount
    const minReviewsToShow = renderCTAInline ? MIN_VISIBLE_REVIEW_COUNT : 0
    const reviewSummary =
        reviewRating != null &&
        reviewCount != null &&
        reviewCount > minReviewsToShow
            ? { rating: reviewRating, count: reviewCount }
            : null

    const starColorForText = reviewStarColorOverride || accentFillColor

    const trackImageCarouselSwipe = useCallback(() => {
        Analytics.TRACK_PDP_IMAGE_CAROUSEL_SWIPE(lpLink.title)
    }, [Analytics, lpLink.title])

    const onVariantSelect = (variant: VLPVariant, isUserSelected: boolean) => {
        setActiveVariant(variant)
        if (
            isCartActionButtonType(landingPage?.button?.type) &&
            landingPage?.button?.linkHash === lpLink.linkHash &&
            isUserSelected
        ) {
            setSelectedCartActionVariantShopifyId(variant.shopifyId)
        }
    }

    return (
        <NavGridInnerContainer
            ref={productInnerContainer}
            isInOuterScrollContainer={isInOuterScrollContainer}
            singleColumn={singleColumn}
        >
            <GalleryColumn>
                <GalleryPin pinned={embedded && !singleColumn}>
                    <ProductImageCarousel
                        key={carouselInstanceKey}
                        imageUrls={carouselImageUrls}
                        onSlideChange={trackImageCarouselSwipe}
                        maxImageHeight={maxImageHeight}
                        prioritizeHero={prioritizeHeroImage}
                    />
                </GalleryPin>
            </GalleryColumn>
            <ProductSurface singleColumn={singleColumn}>
                <ProductHeader flush={flushContent} singleColumn={singleColumn}>
                    <TitlePriceRow>
                        <TitleCell>
                            <div
                                onClick={onTitleClick}
                                style={{
                                    ...getTypographyRoleStyle('productTitle', {
                                        fontWeight: 400,
                                        lineHeight: 1.3,
                                        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                                    }),
                                    fontSize: scaledFontSize(18),
                                    cursor: onTitleClick
                                        ? 'pointer'
                                        : undefined,
                                }}
                            >
                                {displayData.title ?? lpLink?.title}
                            </div>
                        </TitleCell>
                        {reviewSummary && (
                            <ReviewsCell
                                onClick={
                                    showReviews ? onReviewsClick : undefined
                                }
                                style={{
                                    cursor: showReviews ? 'pointer' : undefined,
                                }}
                            >
                                <ReviewsStars
                                    rating={reviewSummary.rating}
                                    count={reviewSummary.count}
                                    starSize={18}
                                    fontSize={14}
                                    starColor={starColorForText}
                                    textColor={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`}
                                />
                            </ReviewsCell>
                        )}
                        <PriceCell>
                            <PriceMainRow
                                price={displayPrice}
                                discountPercentage={displayDiscountPercentage}
                            />
                            {displayComparePrice !== null &&
                                displayComparePrice > displayPrice && (
                                    <div style={{ marginTop: 4 }}>
                                        <CompareAtPriceRow
                                            price={displayPrice}
                                            compareAtPrice={displayComparePrice}
                                        />
                                    </div>
                                )}
                        </PriceCell>
                    </TitlePriceRow>
                    {productValueBadges.length > 0 && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                marginTop: 8,
                            }}
                        >
                            {productValueBadges}
                        </div>
                    )}
                </ProductHeader>
                <ProductBuyBox flush={flushContent} singleColumn={singleColumn}>
                    {showVariantSelector && (
                        <div style={{ marginTop: 24 }}>
                            {productOptionGuide?.imageUrl ? (
                                <ProductOptionGuideRow
                                    guide={productOptionGuide}
                                />
                            ) : null}
                            <VariantSelector
                                // Remount when the product or storefront default
                                // changes; selectedOptions is mount-only state.
                                key={`${lpLink.linkHash}:${lpLink.renderedDefaultVariantId ?? ''}`}
                                onVariantSelect={onVariantSelect}
                                link={lpLink}
                                initialSelectedVariantShopifyId={
                                    activeVariant?.shopifyId ?? null
                                }
                            />
                            {activeVariant?.available && (
                                <div style={{ marginTop: 16 }}>
                                    <InStockIndicator />
                                </div>
                            )}
                            {activeVariant?.available &&
                                onSellingPlanSelect && (
                                    <PdpSubscriptionSelector
                                        // Remount per product so a prior
                                        // product's remembered frequency doesn't
                                        // carry into this one's preview. Prefixed
                                        // so it doesn't collide with the sibling
                                        // VariantSelector's linkHash key.
                                        key={`selling-plan-${lpLink.linkHash}`}
                                        sellingPlanGroups={sellingPlanGroups}
                                        priceCents={price}
                                        subscriptionBasePriceCents={
                                            subscriptionBasePriceCents
                                        }
                                        selectedSellingPlanId={
                                            selectedSellingPlanId
                                        }
                                        requiresSellingPlan={
                                            requiresSellingPlan
                                        }
                                        onSelect={onSellingPlanSelect}
                                    />
                                )}
                            {renderCTAInline && activeVariant && (
                                <div style={{ marginTop: 16 }}>
                                    {inlineCTAActions.length > 0 && (
                                        <>
                                            {(() => {
                                                {
                                                    /* Only show the first action if the selected variant is out of stock. */
                                                }
                                                const actionsToShow =
                                                    activeVariant.available ===
                                                    false
                                                        ? inlineCTAActions.slice(
                                                              0,
                                                              1,
                                                          )
                                                        : inlineCTAActions
                                                const bothPrimaryActions =
                                                    inlineCTAActions.includes(
                                                        INLINE_CTA_ACTION.ADD_TO_CART,
                                                    ) &&
                                                    inlineCTAActions.includes(
                                                        INLINE_CTA_ACTION.BUY_NOW,
                                                    )
                                                const ctaVariant =
                                                    buildCartVariant(
                                                        activeVariant,
                                                        lpLink,
                                                        landingPage?.discount ??
                                                            null,
                                                        !!selectedSellingPlanId,
                                                    )
                                                const sideBySide =
                                                    bothPrimaryActions
                                                const {
                                                    addToCart: atcButtonText,
                                                    buyNow: buyNowButtonText,
                                                } = resolveInlineCtaButtonText({
                                                    actions: inlineCTAActions,
                                                    buttonText:
                                                        inlineButtonText,
                                                })
                                                return (
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            display: 'flex',
                                                            gap: 16,
                                                            flexDirection:
                                                                sideBySide
                                                                    ? 'row'
                                                                    : 'column',
                                                        }}
                                                    >
                                                        {actionsToShow.map(
                                                            (action, index) => {
                                                                switch (
                                                                    action
                                                                ) {
                                                                    case INLINE_CTA_ACTION.ADD_TO_CART:
                                                                        return (
                                                                            <ProductDrawerCTA
                                                                                key={`${action}-${index}`}
                                                                                variant={
                                                                                    ctaVariant
                                                                                }
                                                                                action={
                                                                                    INLINE_CTA_ACTION.ADD_TO_CART
                                                                                }
                                                                                sellingPlanId={
                                                                                    selectedSellingPlanId
                                                                                }
                                                                                buttonText={
                                                                                    atcButtonText
                                                                                }
                                                                                backgroundColor={
                                                                                    bothPrimaryActions
                                                                                        ? secondaryCtaFillColor
                                                                                        : undefined
                                                                                }
                                                                                textColor={
                                                                                    bothPrimaryActions
                                                                                        ? secondaryCtaTextColor
                                                                                        : undefined
                                                                                }
                                                                                border={
                                                                                    bothPrimaryActions
                                                                                        ? `var(${SECONDARY_CTA_BORDER_VAR})`
                                                                                        : undefined
                                                                                }
                                                                            />
                                                                        )
                                                                    case INLINE_CTA_ACTION.BUY_NOW:
                                                                        return (
                                                                            <ProductDrawerCTA
                                                                                key={`${action}-${index}`}
                                                                                variant={
                                                                                    ctaVariant
                                                                                }
                                                                                action={
                                                                                    INLINE_CTA_ACTION.BUY_NOW
                                                                                }
                                                                                sellingPlanId={
                                                                                    selectedSellingPlanId
                                                                                }
                                                                                buttonText={
                                                                                    buyNowButtonText
                                                                                }
                                                                                loader={
                                                                                    true
                                                                                }
                                                                                useShopPay={
                                                                                    displayShopPayButtons
                                                                                }
                                                                            />
                                                                        )
                                                                    default:
                                                                        return null
                                                                }
                                                            },
                                                        )}
                                                    </div>
                                                )
                                            })()}
                                            {onTitleClick &&
                                                !showBodyContent && (
                                                    <FullDetailsLink
                                                        onClick={onTitleClick}
                                                    >
                                                        view full product
                                                        details →
                                                    </FullDetailsLink>
                                                )}
                                            <BrandChatQuestions
                                                productId={lpLink.shopifyId}
                                                title={lpLink.title}
                                                enabled={showBrandChat}
                                            />
                                            {activeVariant.available && (
                                                <BrandBenefits
                                                    benefits={(() => {
                                                        const lastAction =
                                                            inlineCTAActions.at(
                                                                -1,
                                                            )
                                                        if (
                                                            lastAction ===
                                                            INLINE_CTA_ACTION.BUY_NOW
                                                        ) {
                                                            return landingPage.buyNowBrandBenefits
                                                        }
                                                        if (
                                                            lastAction ===
                                                            INLINE_CTA_ACTION.ADD_TO_CART
                                                        ) {
                                                            return landingPage.addToCartBrandBenefits
                                                        }
                                                        return []
                                                    })()}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </ProductBuyBox>

                {(showReviews ||
                    (displayData.bodyContent && showBodyContent)) && (
                    <ProductContentBlockWrapper style={{ marginTop: 24 }}>
                        <ProductContentBlock
                            link={displayLink}
                            variantShopifyProductId={
                                activeVariant?.shopifyProductId
                            }
                            showBodyContent={showBodyContent}
                            showReviews={showReviews}
                        />
                    </ProductContentBlockWrapper>
                )}
            </ProductSurface>
            <FooterRow>
                <PoweredByFooter
                    displayPoweredByFooter={displayPoweredByFooter}
                />
            </FooterRow>
        </NavGridInnerContainer>
    )
}

/** Return shape of {@link useProductData} */
export type ProductData = {
    activeVariant: VLPVariant | null
    setActiveVariant: Dispatch<SetStateAction<VLPVariant | null>>
    reviewsRef: MutableRefObject<HTMLDivElement | null>
    price: number
    comparePrice: number | null
    discountPercentage: number | null
    /** Selling plan chosen on the PDP, applied when the item is added to cart. */
    selectedSellingPlanId: string | null
    setSelectedSellingPlanId: Dispatch<SetStateAction<string | null>>
}

const variantIdFromQuery = (
    value: string | string[] | undefined,
): string | null => {
    const raw = Array.isArray(value) ? value[0] : value
    return raw || null
}

export const useProductData = (
    lpLink: ApiShopperLandingPageLink,
    landingPage: LandingPageModel | null,
    options?: ActiveLPLinkOptions | null,
): ProductData => {
    // `router.query` is in the SSR payload, so the server and the hydration
    // render select the same variant. `window.location` is empty on the server.
    const router = useRouter()
    const variantFromQuery = variantIdFromQuery(router.query.variant)
    const initialVariantShopifyId =
        variantFromQuery ?? options?.initialVariantShopifyId ?? null

    const defaultVariant = useMemo(
        () =>
            resolveDefaultPdpVariant(lpLink, {
                initialVariantShopifyId,
                preferAvailable: options?.preferAvailable,
                preferredOptionName: options?.preferredOptionName,
            }),
        [
            lpLink,
            initialVariantShopifyId,
            options?.preferAvailable,
            options?.preferredOptionName,
        ],
    )

    const { displaySubscriptionOptions, defaultPurchaseOption } =
        useContext(CustomizationContext)

    // The purchase option a product defaults to. Subscription-only products
    // always get their first plan; otherwise the brand default applies.
    const defaultSellingPlanIdFor = (variant: VLPVariant | null) =>
        getDefaultPdpSellingPlanId({
            sellingPlanGroups: getSellingPlanGroups(
                lpLink,
                variant?.shopifyProductId,
                variant?.shopifyId,
            ),
            subscriptionBasePriceCents: variant?.price.price ?? null,
            defaultPurchaseOption,
            displaySubscriptionOptions,
            requiresSellingPlan: getRequiresSellingPlan(
                lpLink,
                variant?.shopifyProductId,
            ),
        })

    const [activeVariant, setActiveVariant] = useState<VLPVariant | null>(
        defaultVariant,
    )
    // Reset the active variant whenever the default recalculates (e.g. a new
    // lpLink/variantInfo), overriding any prior user selection.
    const [prevDefaultVariant, setPrevDefaultVariant] = useState(defaultVariant)
    if (prevDefaultVariant !== defaultVariant) {
        setPrevDefaultVariant(defaultVariant)
        setActiveVariant(defaultVariant)
    }
    const [selectedSellingPlanId, setSelectedSellingPlanId] = useState<
        string | null
    >(() => defaultSellingPlanIdFor(defaultVariant))
    const [prevLinkHash, setPrevLinkHash] = useState(lpLink.linkHash)
    if (prevLinkHash !== lpLink.linkHash) {
        setPrevLinkHash(lpLink.linkHash)
        setSelectedSellingPlanId(defaultSellingPlanIdFor(defaultVariant))
    }
    const reviewsRef = useRef<HTMLDivElement>(null)

    const {
        price,
        compareAtPrice: comparePrice,
        discountPercentage,
    } = useMemo(
        () =>
            getProductPrices(
                lpLink,
                landingPage?.discount ?? null,
                activeVariant,
            ),
        [activeVariant, lpLink, landingPage],
    )

    // Drop a selling plan id that is no longer subscribable for the current
    // variant so the CTA and cart can't subscribe with a stale id while the
    // selector and hero show one-time. Also force a plan when the variant is
    // subscription-only. Falls back to the variant's default (the brand's
    // default plan when it still applies, otherwise one-time).
    const subscriptionBasePriceCents = activeVariant?.price.price ?? price
    const correctedVariant = getSubscriptionOnlyVariantCorrection(
        lpLink,
        activeVariant,
    )
    if (correctedVariant) {
        setActiveVariant(correctedVariant)
    }
    // Only setState when the value changes — calling setState during render with
    // the same value (e.g. null → null for a subscription-only product with no
    // subscribable plans) re-triggers render and loops forever.
    const nextSelectedSellingPlanId = getAdjustedSelectedSellingPlanId({
        selectedSellingPlanId,
        sellingPlanGroups: getSellingPlanGroups(
            lpLink,
            activeVariant?.shopifyProductId,
            activeVariant?.shopifyId,
        ),
        subscriptionBasePriceCents,
        requiresSellingPlan: getRequiresSellingPlan(
            lpLink,
            activeVariant?.shopifyProductId,
        ),
        defaultSellingPlanId: defaultSellingPlanIdFor(activeVariant),
    })
    if (nextSelectedSellingPlanId !== selectedSellingPlanId) {
        setSelectedSellingPlanId(nextSelectedSellingPlanId)
    }

    return {
        activeVariant,
        setActiveVariant,
        reviewsRef,
        price,
        comparePrice,
        discountPercentage,
        selectedSellingPlanId,
        setSelectedSellingPlanId,
    }
}
